import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input, Labeled } from "../Rentals";
import { PhoneField } from "../../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../../lib/phone";
import { CurrencyInput } from "../../components/CurrencyInput";
import { PaydayEnvBadge } from "../../components/PaydayEnvBadge";
import { formatISK, parseCurrencyInput } from "../../lib/currency";

const bookingTypes = [
  { value: "transport", label: "Transport" },
  { value: "tour", label: "Tour" },
  { value: "rental", label: "Rental" },
];

const transportSubtypes = [
  ["airport_transfer", "Airport transfer"],
  ["private_driver", "Private driver"],
  ["custom_transport", "Custom transport"],
];

const tourSubtypes = [
  ["private_day_tour_8h", "Private day tour 8h"],
  ["private_day_tour_10h", "Private day tour 10h"],
  ["custom_tour", "Custom tour"],
];

const paymentMethods = ["cash", "card_manual", "bank_transfer", "rapyd_link", "unpaid"];
const paymentStatuses = ["unpaid", "paid", "partially_paid"];
const bookingStatuses = ["pending", "confirmed", "cancelled"];
const autoPaydayModes = ["b2b", "cash_manual"];
const autoStatusModes = ["b2b", "cash_manual", "online_card"];

function defaultVat(type) {
  return type === "rental" ? 24 : 11;
}

function emptyLineItem() {
  return { label: "Base price", amount: "" };
}

function validateManualBooking(form) {
  const errors = {};
  if (!String(form.customer.full_name || "").trim()) errors.full_name = "Full name is required.";
  if (!String(form.customer.email || "").trim()) errors.email = "Email is required.";

  if (form.booking_type === "transport") {
    if (!String(form.service_details.subtype || "").trim()) errors.transport_subtype = "Service subtype is required.";
    if (!String(form.service_details.pickup_location || "").trim()) errors.pickup_location = "Pickup location is required.";
    if (!String(form.service_details.dropoff_location || "").trim()) errors.dropoff_location = "Return location is required.";
    if (!String(form.service_details.pickup_date || "").trim()) errors.pickup_date = "Pickup date is required.";
    if (!String(form.service_details.pickup_time || "").trim()) errors.pickup_time = "Pickup time is required.";
  }

  if (form.booking_type === "rental") {
    if (!String(form.service_details.vehicle_id || "").trim()) errors.rental_vehicle = "Vehicle is required.";
    if (!String(form.service_details.pickup_datetime || "").trim()) errors.pickup_datetime = "Pickup datetime is required.";
    if (!String(form.service_details.return_datetime || "").trim()) errors.return_datetime = "Return datetime is required.";
    if (!String(form.service_details.pickup_location || "").trim()) errors.rental_pickup_location = "Pickup location is required.";
    if (!String(form.service_details.return_location || "").trim()) errors.return_location = "Return location is required.";
  }

  if (form.booking_type === "tour") {
    if (!String(form.service_details.subtype || "").trim()) errors.tour_subtype = "Service subtype is required.";
    if (!String(form.service_details.tour_date || "").trim()) errors.tour_date = "Tour date is required.";
    if (!(Number(form.service_details.passenger_count) > 0)) errors.tour_passenger_count = "Passenger count is required.";
  }

  return errors;
}

export default function AdminBookingCreate() {
  const nav = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [paydayConfig, setPaydayConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [form, setForm] = useState({
    booking_type: "transport",
    customer: {
      full_name: "",
      email: "",
      phone: buildPhoneFieldValue(),
      kennitala: "",
      company_name: "",
      invoice_mode: "direct",
      internal_customer_notes: "",
    },
    service_details: {
      subtype: "airport_transfer",
      pickup_location: "",
      dropoff_location: "",
      pickup_date: today,
      pickup_time: "10:00",
      pickup_datetime: `${today}T10:00`,
      return_datetime: `${today}T12:00`,
      return_location: "",
      tour_date: today,
      start_time: "09:00",
      flight_number: "",
      passenger_count: 2,
      luggage_count: "",
      vehicle_id: "",
      driver_id: "",
      same_day_request: false,
      kef_pickup: false,
      custom_itinerary: "",
      renter_age: "",
      driver_count: "",
      mileage_terms: "",
      fuel_terms: "",
      insurance_notes: "",
    },
    pricing: {
      pricing_mode: "manual",
      line_items: [emptyLineItem()],
      base_price_ex_vat: "",
      vat_percentage: defaultVat("transport"),
      vat_amount: "",
      total_price: "",
      currency: "ISK",
    },
    payment: {
      payment_method: "unpaid",
      payment_status: "unpaid",
      amount_paid: "",
      payment_reference: "",
      mark_as_paid_now: false,
    },
    booking_status: "pending",
    options: {
      send_customer_confirmation_email: true,
      create_payday_invoice: false,
      generate_rapyd_payment_link: false,
    },
    internal_notes: "",
  });

  useEffect(() => {
    (async () => {
      const [v, d, p] = await Promise.all([
        api.get("/admin/vehicles"),
        api.get("/admin/drivers"),
        api.get("/admin/payday/config"),
      ]);
      setVehicles(v.data);
      setDrivers(d.data);
      setPaydayConfig(p.data);
    })().catch(() => toast.error("Could not load booking form data"));
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      service_details: {
        ...prev.service_details,
        subtype: prev.booking_type === "tour" ? "private_day_tour_8h" : prev.booking_type === "rental" ? "rental" : "airport_transfer",
      },
      pricing: { ...prev.pricing, vat_percentage: defaultVat(prev.booking_type) },
    }));
  }, [form.booking_type]);

  useEffect(() => {
    setForm((prev) => {
      const mode = prev.customer.invoice_mode;
      const next = {
        ...prev,
        payment: { ...prev.payment },
        options: { ...prev.options },
      };

      if (mode === "online_card") {
        next.payment.payment_method = "rapyd_link";
        next.payment.payment_status = "unpaid";
        next.payment.mark_as_paid_now = false;
        next.payment.amount_paid = "";
        next.options.generate_rapyd_payment_link = true;
        next.options.create_payday_invoice = false;
        next.booking_status = "pending";
      } else if (mode === "b2b") {
        next.options.create_payday_invoice = true;
        next.options.generate_rapyd_payment_link = false;
        if (next.payment.payment_method === "rapyd_link") next.payment.payment_method = "bank_transfer";
        if (!["paid", "partially_paid"].includes(next.payment.payment_status)) next.payment.payment_status = "unpaid";
        next.booking_status = next.payment.payment_status === "paid" ? "confirmed" : "pending";
      } else if (mode === "cash_manual") {
        next.options.create_payday_invoice = true;
        next.options.generate_rapyd_payment_link = false;
        if (!["cash", "card_manual"].includes(next.payment.payment_method)) next.payment.payment_method = "cash";
        next.booking_status = next.payment.payment_status === "paid" ? "confirmed" : "pending";
      }

      return next;
    });
  }, [form.customer.invoice_mode]);

  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev, payment: { ...prev.payment } };
      if (["cash", "card_manual"].includes(prev.payment.payment_method) && prev.payment.mark_as_paid_now) {
        next.payment.payment_status = "paid";
        next.booking_status = "confirmed";
        if (!prev.payment.amount_paid && prev.pricing.total_price) next.payment.amount_paid = prev.pricing.total_price;
      } else if (prev.payment.payment_method === "bank_transfer") {
        next.payment.payment_status = parseCurrencyInput(prev.payment.amount_paid) > 0 ? "partially_paid" : "unpaid";
      } else if (["rapyd_link", "unpaid"].includes(prev.payment.payment_method)) {
        next.payment.payment_status = "unpaid";
      }
      if (next.payment.payment_status === "paid" && prev.options.generate_rapyd_payment_link) {
        next.options = { ...prev.options, generate_rapyd_payment_link: false };
      }
      return next;
    });
  }, [form.payment.payment_method, form.payment.mark_as_paid_now, form.payment.amount_paid, form.pricing.total_price, form.options.generate_rapyd_payment_link]);

  useEffect(() => {
    const base = parseCurrencyInput(form.pricing.base_price_ex_vat) || 0;
    const vatPercentage = Number(form.pricing.vat_percentage || 0);
    const vatAmount = Math.round(base * (vatPercentage / 100));
    const total = base + vatAmount;
    setForm((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        vat_amount: vatAmount ? String(vatAmount) : "",
        total_price: total ? String(total) : "",
      },
    }));
  }, [form.pricing.base_price_ex_vat, form.pricing.vat_percentage]);

  const summary = useMemo(() => {
    const base = parseCurrencyInput(form.pricing.base_price_ex_vat) || 0;
    const vat = parseCurrencyInput(form.pricing.vat_amount) || 0;
    const total = parseCurrencyInput(form.pricing.total_price) || 0;
    const paid = parseCurrencyInput(form.payment.amount_paid) || 0;
    return { base, vat, total, paid, remaining: Math.max(total - paid, 0) };
  }, [form.pricing.base_price_ex_vat, form.pricing.vat_amount, form.pricing.total_price, form.payment.amount_paid]);
  const fieldErrors = useMemo(() => validateManualBooking(form), [form]);
  const phoneMissing = !form.customer.phone?.hasValue;
  const phoneInvalid = form.customer.phone?.hasValue && !form.customer.phone?.e164;
  const customerPhoneError = showValidation && (phoneMissing ? "Phone number is required." : (phoneInvalid ? getPhoneFieldError(form.customer.phone) || "Please enter a valid phone number." : ""));
  const isCustomerPhoneValid = Boolean(form.customer.phone?.e164);
  const onlineCardMode = form.customer.invoice_mode === "online_card";
  const autoPaydayMode = autoPaydayModes.includes(form.customer.invoice_mode);
  const autoStatusMode = autoStatusModes.includes(form.customer.invoice_mode);

  const updateCustomer = (key, value) => setForm((prev) => ({ ...prev, customer: { ...prev.customer, [key]: value } }));
  const updateService = (key, value) => setForm((prev) => ({ ...prev, service_details: { ...prev.service_details, [key]: value } }));
  const updatePricing = (key, value) => setForm((prev) => ({ ...prev, pricing: { ...prev.pricing, [key]: value } }));
  const updatePayment = (key, value) => setForm((prev) => ({ ...prev, payment: { ...prev.payment, [key]: value } }));
  const updateOptions = (key, value) => setForm((prev) => ({ ...prev, options: { ...prev.options, [key]: value } }));

  const addLineItem = () => updatePricing("line_items", [...form.pricing.line_items, { label: "", amount: "" }]);
  const removeLineItem = (index) => updatePricing("line_items", form.pricing.line_items.filter((_, idx) => idx !== index));
  const updateLineItem = (index, key, value) => updatePricing("line_items", form.pricing.line_items.map((item, idx) => idx === index ? { ...item, [key]: value } : item));

  const serviceSubtypeOptions = form.booking_type === "transport" ? transportSubtypes : tourSubtypes;

  const submit = async (allowOverride = false) => {
    setShowValidation(true);
    const phoneError = phoneMissing ? "Phone number is required." : (phoneInvalid ? getPhoneFieldError(form.customer.phone) || "Please enter a valid phone number." : "");
    if (phoneError || Object.keys(fieldErrors).length > 0) {
      updateCustomer("phone", touchPhoneField(form.customer.phone));
      toast.error(phoneError || Object.values(fieldErrors)[0]);
      return;
    }
    const parsedLineItems = form.pricing.line_items
      .map((item) => ({
        label: item.label || "Line item",
        amount: parseCurrencyInput(item.amount) || 0,
      }))
      .filter((item) => item.amount > 0);
    const parsedTotalPrice = parseCurrencyInput(form.pricing.total_price) || 0;
    const payload = {
      booking_type: form.booking_type,
      customer: {
        ...form.customer,
        phone: form.customer.phone.e164 || "",
      },
      service_details: {
        ...form.service_details,
        passenger_count: Number(form.service_details.passenger_count) || 0,
        luggage_count: Number(form.service_details.luggage_count) || 0,
        renter_age: form.service_details.renter_age ? Number(form.service_details.renter_age) : null,
        driver_count: form.service_details.driver_count ? Number(form.service_details.driver_count) : null,
      },
      pricing: {
        ...form.pricing,
        line_items: parsedLineItems.length ? parsedLineItems : (parsedTotalPrice > 0 ? [{ label: "Base price", amount: parsedTotalPrice }] : []),
        base_price_ex_vat: parseCurrencyInput(form.pricing.base_price_ex_vat) || 0,
        vat_percentage: Math.round(Number(form.pricing.vat_percentage) || defaultVat(form.booking_type)),
        vat_amount: parseCurrencyInput(form.pricing.vat_amount) || 0,
        total_price: parsedTotalPrice,
      },
      payment: {
        ...form.payment,
        amount_paid: parseCurrencyInput(form.payment.amount_paid) || 0,
      },
      booking_status: form.booking_status,
      options: { ...form.options, allow_conflict_override: allowOverride },
      internal_notes: form.internal_notes,
    };

    setSaving(true);
    try {
      const { data } = await api.post("/admin/bookings", payload);
      toast.success("Booking created");
      if (data.payday_status === "failed") toast.warning("Booking saved, but Payday invoice creation failed");
      if (data.email_status === "failed") toast.warning("Booking saved, but customer email failed");
      if (data.payment_link) toast.info("Rapyd payment link generated");
      nav("/admin/bookings", { replace: true, state: { createdBookingId: data.booking?.id || data.id } });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "rental_conflict") {
        const confirmOverride = window.confirm(`${detail.message}\n\nCreate the booking anyway?`);
        if (confirmOverride) {
          setSaving(false);
          return submit(true);
        }
      }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-booking-create-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/admin/bookings" data-testid="admin-booking-create-back" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900">
            <ArrowLeft size={14} /> Back to bookings
          </Link>
          <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight">Create manual booking</h1>
          <p className="mt-1 text-sm text-slate-500">Top-right button on the bookings page opens this universal admin creation flow.</p>
          <PaydayEnvBadge config={paydayConfig} testId="admin-booking-create-payday-badge" />
        </div>
        <button onClick={() => submit(false)} disabled={saving || !isCustomerPhoneValid} data-testid="admin-booking-create-submit-top" className="rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {saving ? "Creating…" : "Create booking"}
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.7fr]">
        <div className="space-y-6">
          <Section title="Section A — Booking type">
            <div className="grid gap-3 sm:grid-cols-3" data-testid="manual-booking-type-row">
              {bookingTypes.map((type) => (
                <button key={type.value} type="button" data-testid={`manual-booking-type-${type.value}`} onClick={() => setForm((prev) => ({ ...prev, booking_type: type.value }))} className={`rounded-md border px-4 py-3 text-left text-sm font-semibold ${form.booking_type === type.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800"}`}>
                  {type.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Section B — Customer">
            <div className="grid gap-4 md:grid-cols-2">
              <Labeled label="Full name"><><Input data-testid="manual-booking-full-name" required value={form.customer.full_name} onChange={(e) => updateCustomer("full_name", e.target.value)} />{showValidation && fieldErrors.full_name && <FieldError message={fieldErrors.full_name} />}</></Labeled>
              <Labeled label="Email"><><Input data-testid="manual-booking-email" required type="email" value={form.customer.email} onChange={(e) => updateCustomer("email", e.target.value)} />{showValidation && fieldErrors.email && <FieldError message={fieldErrors.email} />}</></Labeled>
              <Labeled label="Phone"><PhoneField value={form.customer.phone} onChange={(phone) => updateCustomer("phone", phone)} countryTestId="manual-booking-phone-country" numberTestId="manual-booking-phone" errorTestId="manual-booking-phone-error" externalError={customerPhoneError} /></Labeled>
              <Labeled label="Kennitala"><Input data-testid="manual-booking-kennitala" value={form.customer.kennitala} onChange={(e) => updateCustomer("kennitala", e.target.value)} /></Labeled>
              <Labeled label="Company name"><Input data-testid="manual-booking-company" value={form.customer.company_name} onChange={(e) => updateCustomer("company_name", e.target.value)} /></Labeled>
              <Labeled label="Invoice mode">
                <select data-testid="manual-booking-invoice-mode" value={form.customer.invoice_mode} onChange={(e) => updateCustomer("invoice_mode", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                  <option value="direct">direct</option>
                  <option value="b2b">b2b</option>
                  <option value="cash_manual">cash_manual</option>
                  <option value="online_card">online_card</option>
                </select>
              </Labeled>
            </div>
            {autoPaydayMode && <p className="text-xs text-slate-500" data-testid="manual-booking-auto-payday-note">Payday drafts are created automatically for {form.customer.invoice_mode} bookings.</p>}
            {onlineCardMode && <p className="text-xs text-slate-500" data-testid="manual-booking-online-card-note">Online-card bookings always use a full Rapyd payment link and stay unpaid until Rapyd confirms the charge.</p>}
            <Labeled label="Internal customer notes"><textarea rows={3} data-testid="manual-booking-internal-customer-notes" value={form.customer.internal_customer_notes} onChange={(e) => updateCustomer("internal_customer_notes", e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" /></Labeled>
          </Section>

          <Section title="Section C — Service details">
            {form.booking_type === "transport" && (
              <div className="grid gap-4 md:grid-cols-2">
                <Labeled label="Service subtype"><><select data-testid="manual-booking-transport-subtype" value={form.service_details.subtype} onChange={(e) => updateService("subtype", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">{transportSubtypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{showValidation && fieldErrors.transport_subtype && <FieldError message={fieldErrors.transport_subtype} />}</></Labeled>
                <Labeled label="Pickup location"><><Input data-testid="manual-booking-pickup-location" value={form.service_details.pickup_location} onChange={(e) => updateService("pickup_location", e.target.value)} />{showValidation && fieldErrors.pickup_location && <FieldError message={fieldErrors.pickup_location} />}</></Labeled>
                <Labeled label="Drop-off location"><><Input data-testid="manual-booking-dropoff-location" value={form.service_details.dropoff_location} onChange={(e) => updateService("dropoff_location", e.target.value)} />{showValidation && fieldErrors.dropoff_location && <FieldError message={fieldErrors.dropoff_location} />}</></Labeled>
                <Labeled label="Pickup date"><><Input data-testid="manual-booking-pickup-date" type="date" value={form.service_details.pickup_date} onChange={(e) => updateService("pickup_date", e.target.value)} />{showValidation && fieldErrors.pickup_date && <FieldError message={fieldErrors.pickup_date} />}</></Labeled>
                <Labeled label="Pickup time"><><Input data-testid="manual-booking-pickup-time" type="time" value={form.service_details.pickup_time} onChange={(e) => updateService("pickup_time", e.target.value)} />{showValidation && fieldErrors.pickup_time && <FieldError message={fieldErrors.pickup_time} />}</></Labeled>
                <Labeled label="Flight number"><Input data-testid="manual-booking-flight-number" value={form.service_details.flight_number} onChange={(e) => updateService("flight_number", e.target.value)} /></Labeled>
                <Labeled label="Passenger count"><Input data-testid="manual-booking-passenger-count" type="number" min="1" value={form.service_details.passenger_count} onChange={(e) => updateService("passenger_count", e.target.value)} /></Labeled>
                <Labeled label="Luggage count"><Input data-testid="manual-booking-luggage-count" type="number" min="0" value={form.service_details.luggage_count} onChange={(e) => updateService("luggage_count", e.target.value)} /></Labeled>
                <Labeled label="Vehicle"><select data-testid="manual-booking-vehicle" value={form.service_details.vehicle_id} onChange={(e) => updateService("vehicle_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="">Auto / unassigned</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Labeled>
                <Labeled label="Driver"><select data-testid="manual-booking-driver" value={form.service_details.driver_id} onChange={(e) => updateService("driver_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="">Auto / unassigned</option>{drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Labeled>
                <Toggle label="Same-day request" checked={form.service_details.same_day_request} onChange={(checked) => updateService("same_day_request", checked)} testId="manual-booking-same-day-request" />
                <Toggle label="KEF pickup" checked={form.service_details.kef_pickup} onChange={(checked) => updateService("kef_pickup", checked)} testId="manual-booking-kef-pickup" />
              </div>
            )}

            {form.booking_type === "rental" && (
              <div className="grid gap-4 md:grid-cols-2">
                <Labeled label="Vehicle"><><select data-testid="manual-booking-rental-vehicle" value={form.service_details.vehicle_id} onChange={(e) => updateService("vehicle_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="">Select vehicle</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>{showValidation && fieldErrors.rental_vehicle && <FieldError message={fieldErrors.rental_vehicle} />}</></Labeled>
                <Labeled label="Pickup datetime"><><Input data-testid="manual-booking-pickup-datetime" type="datetime-local" value={form.service_details.pickup_datetime} onChange={(e) => updateService("pickup_datetime", e.target.value)} />{showValidation && fieldErrors.pickup_datetime && <FieldError message={fieldErrors.pickup_datetime} />}</></Labeled>
                <Labeled label="Return datetime"><><Input data-testid="manual-booking-return-datetime" type="datetime-local" value={form.service_details.return_datetime} onChange={(e) => updateService("return_datetime", e.target.value)} />{showValidation && fieldErrors.return_datetime && <FieldError message={fieldErrors.return_datetime} />}</></Labeled>
                <Labeled label="Pickup location"><><Input data-testid="manual-booking-rental-pickup-location" value={form.service_details.pickup_location} onChange={(e) => updateService("pickup_location", e.target.value)} />{showValidation && fieldErrors.rental_pickup_location && <FieldError message={fieldErrors.rental_pickup_location} />}</></Labeled>
                <Labeled label="Return location"><><Input data-testid="manual-booking-rental-return-location" value={form.service_details.return_location} onChange={(e) => updateService("return_location", e.target.value)} />{showValidation && fieldErrors.return_location && <FieldError message={fieldErrors.return_location} />}</></Labeled>
                <Labeled label="Renter age"><Input data-testid="manual-booking-renter-age" type="number" min="18" value={form.service_details.renter_age} onChange={(e) => updateService("renter_age", e.target.value)} /></Labeled>
                <Labeled label="Driver count"><Input data-testid="manual-booking-driver-count" type="number" min="1" value={form.service_details.driver_count} onChange={(e) => updateService("driver_count", e.target.value)} /></Labeled>
                <Labeled label="Mileage terms"><Input data-testid="manual-booking-mileage-terms" value={form.service_details.mileage_terms} onChange={(e) => updateService("mileage_terms", e.target.value)} /></Labeled>
                <Labeled label="Fuel terms"><Input data-testid="manual-booking-fuel-terms" value={form.service_details.fuel_terms} onChange={(e) => updateService("fuel_terms", e.target.value)} /></Labeled>
                <Labeled label="Insurance notes"><textarea rows={3} data-testid="manual-booking-insurance-notes" value={form.service_details.insurance_notes} onChange={(e) => updateService("insurance_notes", e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" /></Labeled>
              </div>
            )}

            {form.booking_type === "tour" && (
              <div className="grid gap-4 md:grid-cols-2">
                <Labeled label="Service subtype"><><select data-testid="manual-booking-tour-subtype" value={form.service_details.subtype} onChange={(e) => updateService("subtype", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">{tourSubtypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{showValidation && fieldErrors.tour_subtype && <FieldError message={fieldErrors.tour_subtype} />}</></Labeled>
                <Labeled label="Tour date"><><Input data-testid="manual-booking-tour-date" type="date" value={form.service_details.tour_date} onChange={(e) => updateService("tour_date", e.target.value)} />{showValidation && fieldErrors.tour_date && <FieldError message={fieldErrors.tour_date} />}</></Labeled>
                <Labeled label="Start time"><Input data-testid="manual-booking-tour-start-time" type="time" value={form.service_details.start_time} onChange={(e) => updateService("start_time", e.target.value)} /></Labeled>
                <Labeled label="Passenger count"><><Input data-testid="manual-booking-tour-passenger-count" type="number" min="1" value={form.service_details.passenger_count} onChange={(e) => updateService("passenger_count", e.target.value)} />{showValidation && fieldErrors.tour_passenger_count && <FieldError message={fieldErrors.tour_passenger_count} />}</></Labeled>
                <Labeled label="Vehicle"><select data-testid="manual-booking-tour-vehicle" value={form.service_details.vehicle_id} onChange={(e) => updateService("vehicle_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="">Auto / unassigned</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select></Labeled>
                <Labeled label="Driver"><select data-testid="manual-booking-tour-driver" value={form.service_details.driver_id} onChange={(e) => updateService("driver_id", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="">Auto / unassigned</option>{drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Labeled>
                <Labeled label="Pickup location"><Input data-testid="manual-booking-tour-pickup-location" value={form.service_details.pickup_location} onChange={(e) => updateService("pickup_location", e.target.value)} /></Labeled>
                <Labeled label="Custom itinerary"><textarea rows={4} data-testid="manual-booking-custom-itinerary" value={form.service_details.custom_itinerary} onChange={(e) => updateService("custom_itinerary", e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" /></Labeled>
              </div>
            )}
          </Section>

          <Section title="Section D — Pricing">
            <div className="grid gap-4 md:grid-cols-2">
              <Labeled label="Pricing mode"><select data-testid="manual-booking-pricing-mode" value={form.pricing.pricing_mode} onChange={(e) => updatePricing("pricing_mode", e.target.value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"><option value="manual">manual</option><option value="calculated">calculated</option></select></Labeled>
              <Labeled label="VAT %"><Input data-testid="manual-booking-vat-percentage" type="number" min="0" max="100" value={form.pricing.vat_percentage} onChange={(e) => updatePricing("vat_percentage", e.target.value)} /></Labeled>
              <Labeled label="Base price ex VAT"><CurrencyInput data-testid="manual-booking-base-price" value={form.pricing.base_price_ex_vat} onChange={(value) => updatePricing("base_price_ex_vat", value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" /></Labeled>
              <Labeled label="VAT amount"><CurrencyInput data-testid="manual-booking-vat-amount" value={form.pricing.vat_amount} onChange={(value) => updatePricing("vat_amount", value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" /></Labeled>
              <Labeled label="Total price"><CurrencyInput data-testid="manual-booking-total-price" value={form.pricing.total_price} onChange={(value) => updatePricing("total_price", value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" /></Labeled>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">Line items</p>
                <button type="button" onClick={addLineItem} data-testid="manual-booking-add-line-item" className="inline-flex items-center gap-2 text-xs font-semibold text-sky-700"><Plus size={12} /> Add line</button>
              </div>
              {form.pricing.line_items.map((item, index) => (
                <div key={`${index}-${item.label}`} className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
                  <Input data-testid={`manual-booking-line-label-${index}`} value={item.label} onChange={(e) => updateLineItem(index, "label", e.target.value)} placeholder="Label" />
                  <CurrencyInput data-testid={`manual-booking-line-amount-${index}`} value={item.amount} onChange={(value) => updateLineItem(index, "amount", value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900" placeholder="Amount" />
                  <button type="button" onClick={() => removeLineItem(index)} data-testid={`manual-booking-line-remove-${index}`} className="inline-flex items-center justify-center rounded-md border border-slate-300 px-3 text-slate-500 hover:bg-slate-50"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Section E — Payment">
            <div className="grid gap-4 md:grid-cols-2">
              <Labeled label="Payment method"><select data-testid="manual-booking-payment-method" value={form.payment.payment_method} onChange={(e) => updatePayment("payment_method", e.target.value)} disabled={onlineCardMode} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100">{paymentMethods.map((method) => <option key={method} value={method}>{method}</option>)}</select></Labeled>
              <Labeled label="Payment status"><select data-testid="manual-booking-payment-status" value={form.payment.payment_status} onChange={(e) => updatePayment("payment_status", e.target.value)} disabled={onlineCardMode} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100">{paymentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></Labeled>
              <Labeled label="Amount paid"><CurrencyInput data-testid="manual-booking-amount-paid" value={form.payment.amount_paid} onChange={(value) => updatePayment("amount_paid", value)} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100" disabled={onlineCardMode} /></Labeled>
              <Labeled label="Payment reference"><Input data-testid="manual-booking-payment-reference" value={form.payment.payment_reference} onChange={(e) => updatePayment("payment_reference", e.target.value)} /></Labeled>
            </div>
            <Toggle label="Mark as paid now" checked={form.payment.mark_as_paid_now} onChange={(checked) => updatePayment("mark_as_paid_now", checked)} testId="manual-booking-mark-paid-now" disabled={onlineCardMode} />
          </Section>

          <Section title="Section F — Booking status">
            <Labeled label="Status"><select data-testid="manual-booking-status" value={form.booking_status} onChange={(e) => setForm((prev) => ({ ...prev, booking_status: e.target.value }))} disabled={autoStatusMode} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100">{bookingStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</select></Labeled>
            {autoStatusMode && <p className="text-xs text-slate-500" data-testid="manual-booking-status-note">Status is controlled automatically for {form.customer.invoice_mode} bookings.</p>}
          </Section>

          <Section title="Section G — Admin options">
            <div className="grid gap-3 md:grid-cols-2">
              <Toggle label="Send customer confirmation email" checked={form.options.send_customer_confirmation_email} onChange={(checked) => updateOptions("send_customer_confirmation_email", checked)} testId="manual-booking-send-email" />
              <Toggle label="Create Payday invoice" checked={autoPaydayMode ? true : form.options.create_payday_invoice} onChange={(checked) => updateOptions("create_payday_invoice", checked)} testId="manual-booking-create-payday" disabled={autoPaydayMode || onlineCardMode} />
              <Toggle label="Generate Rapyd payment link" checked={onlineCardMode ? true : form.options.generate_rapyd_payment_link} onChange={(checked) => updateOptions("generate_rapyd_payment_link", checked)} testId="manual-booking-generate-rapyd" disabled={onlineCardMode || form.payment.payment_status === "paid"} />
            </div>
            <Labeled label="Internal notes"><textarea rows={4} data-testid="manual-booking-internal-notes" value={form.internal_notes} onChange={(e) => setForm((prev) => ({ ...prev, internal_notes: e.target.value }))} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" /></Labeled>
          </Section>

          <button onClick={() => submit(false)} disabled={saving || !isCustomerPhoneValid} data-testid="admin-booking-create-submit-bottom" className="w-full rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {saving ? "Creating…" : "Create booking"}
          </button>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-8 self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="manual-booking-summary-card">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">Live booking summary</p>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <SummaryRow label="Booking type" value={form.booking_type} />
              <SummaryRow label="Subtype" value={form.service_details.subtype} />
              <SummaryRow label="Invoice mode" value={form.customer.invoice_mode} />
              <SummaryRow label="Payment method" value={form.payment.payment_method} />
              <SummaryRow label="Payment status" value={form.payment.payment_status} />
              <SummaryRow label="Base ex VAT" value={formatISK(summary.base)} />
              <SummaryRow label={`VAT (${form.pricing.vat_percentage || 0}%)`} value={formatISK(summary.vat)} />
              <SummaryRow label="Total" value={formatISK(summary.total)} strong />
              <SummaryRow label="Amount paid" value={formatISK(summary.paid)} />
              <SummaryRow label="Remaining" value={formatISK(summary.remaining)} strong />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{title}</p>
      </div>
      {children}
    </section>
  );
}

function Toggle({ label, checked, onChange, testId, disabled = false }) {
  return (
    <label className="flex items-center gap-3 text-sm text-slate-700" data-testid={testId}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 disabled:opacity-60" />
      <span className={disabled ? "text-slate-400" : ""}>{label}</span>
    </label>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <span className={strong ? "font-semibold text-slate-900" : "text-slate-900"}>{value || "—"}</span>
    </div>
  );
}

function FieldError({ message }) {
  return <p className="mt-1 text-xs font-medium text-red-600">{message}</p>;
}